import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LockKeyhole, Mail } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function PasswordResetPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestPasswordReset, validateResetToken, confirmReset } = useAuth();
  const token = useMemo(() => new URLSearchParams(location.search).get("token"), [location.search]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenStatus, setTokenStatus] = useState({ checking: Boolean(token), valid: false, user: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;

    const checkToken = async () => {
      const result = await validateResetToken(token);
      if (result.success) {
        setTokenStatus({ checking: false, valid: true, user: result.data.user });
      } else {
        setTokenStatus({ checking: false, valid: false, user: null });
        toast.error(result.error);
      }
    };

    checkToken();
  }, [token, validateResetToken]);

  const handleRequest = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    const result = await requestPasswordReset(email);
    setSubmitting(false);
    if (result.success) {
      toast.success("Reset request recorded. In this version, an administrator can also generate the reset link from User Management.");
    } else {
      toast.error(result.error);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    if (!password || password !== confirmPassword) {
      toast.error("Passwords must match before you continue.");
      return;
    }
    setSubmitting(true);
    const result = await confirmReset(token, password);
    setSubmitting(false);
    if (result.success) {
      toast.success("Password updated successfully.");
      navigate("/login");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F5F7FA] px-6 py-12">
      <div className="w-full max-w-lg rounded-[32px] border border-[#E2E8F0] bg-white p-8 shadow-sm">
        <Logo size="large" />
        <div className="mt-6 space-y-2">
          <h1 className="font-['Manrope'] text-3xl font-semibold text-[#0F172A]">
            {token ? "Create a new password" : "Request password reset"}
          </h1>
          <p className="text-[#64748B]">
            {token
              ? "Use your secure reset token to complete the password update."
              : "Submit your email to register a password reset request. Admins can also generate reset links directly from the user management screen."}
          </p>
        </div>

        {!token && (
          <form className="mt-8 space-y-5" onSubmit={handleRequest}>
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@carefleettransport.co.uk"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="h-12 w-full bg-[#163D6B] hover:bg-[#0E2E53]">
              {submitting ? "Submitting..." : "Request reset"}
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={() => navigate("/login")}>
              Back to login
            </Button>
          </form>
        )}

        {token && (
          <div className="mt-8">
            {tokenStatus.checking ? (
              <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#64748B]">
                Checking reset token...
              </div>
            ) : tokenStatus.valid ? (
              <form className="space-y-5" onSubmit={handleReset}>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                  Resetting password for <strong>{tokenStatus.user?.email}</strong>
                </div>
                <div>
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative mt-2">
                    <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
                    <Input
                      id="new-password"
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="mt-2"
                  />
                </div>
                <Button type="submit" disabled={submitting} className="h-12 w-full bg-[#163D6B] hover:bg-[#0E2E53]">
                  {submitting ? "Updating..." : "Update password"}
                </Button>
              </form>
            ) : (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                This reset token is invalid or has expired. Ask an administrator to generate a new reset link.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
